import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { FaUsers, FaBars, FaTimes, FaAward, FaStar, FaMapMarkerAlt, FaArrowRight, FaMoneyBillWave, FaSeedling, FaBook, FaCamera, FaHeart } from 'react-icons/fa';
import { CalendarDays } from 'lucide-react';

const CalendarDaysIcon = (props) => <CalendarDays size="1em" strokeWidth={2.2} {...props} />;

// Custom left-facing cruise ship with 3 smokestacks
const CruiseShipIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    width="1em"
    height="1em"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    {/* Three smokestacks (middle one taller) */}
    <rect x="8.2" y="4" width="1.5" height="3.4" rx="0.3" />
    <rect x="10.7" y="2.8" width="1.5" height="4.6" rx="0.3" />
    <rect x="13.2" y="4" width="1.5" height="3.4" rx="0.3" />
    {/* Top bridge deck */}
    <rect x="6.5" y="7.4" width="10.5" height="1.9" rx="0.3" />
    {/* Middle deck with portholes via small rect cutouts are not used — use a solid wider bar */}
    <rect x="4.5" y="9.3" width="14.5" height="2.6" rx="0.3" />
    {/* Hull — clear left-pointing bow */}
    <path d="M 1.5 11.9 L 21 11.9 L 19.8 16 L 4 16 L 1.5 11.9 Z" />
    {/* Waves beneath */}
    <path d="M 2 17.4 Q 4 18.6 6 17.4 T 10 17.4 T 14 17.4 T 18 17.4 T 22 17.4" stroke="currentColor" strokeWidth="0.8" fill="none" strokeLinecap="round" />
  </svg>
);

// Founding Advisor Spotlight Component
const FoundingAdvisorSpotlight = ({ navigate }) => {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdvisors = async () => {
      try {
        const res = await fetch('/api/professionals/founding-advisors');
        if (res.ok) {
          const data = await res.json();
          setAdvisors(data);
        }
      } catch (err) {
        console.error('Error fetching advisors:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAdvisors();
  }, []);

  if (loading || advisors.length === 0) return null;

  const getInitials = (name) =>
    name.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const getSpecialtyLabel = (type) => {
    const labels = {
      financial_advisor: 'Financial Advisor',
      estate_attorney: 'Estate Attorney',
      tax_professional: 'Tax Professional',
      family_therapist: 'Family Therapist',
      insurance_specialist: 'Insurance Specialist',
      real_estate: 'Real Estate',
      other: 'Professional Advisor'
    };
    return labels[type] || 'Professional Advisor';
  };

  return (
    <section className="py-16 bg-gradient-to-br from-amber-50 via-white to-amber-50" data-testid="founding-advisor-spotlight">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <FaAward /> Founding Advisors
          </div>
          <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Meet Our Founding Advisors
          </h3>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Trusted professionals who joined Legacy Forge early to help families build lasting legacies.
          </p>
        </div>

        <div className={`grid gap-8 max-w-4xl mx-auto ${advisors.length === 1 ? 'grid-cols-1 max-w-md' : 'grid-cols-1 md:grid-cols-2'}`}>
          {advisors.map((advisor) => (
            <div
              key={advisor.professional_id || advisor.profile_id}
              className="bg-white rounded-2xl shadow-lg border border-amber-100 overflow-hidden hover:shadow-xl transition-shadow"
              data-testid={`founding-advisor-card-${advisor.name?.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-white font-semibold text-sm">
                  <FaAward /> Founding Advisor
                </div>
                <div className="flex items-center gap-1 text-amber-100 text-sm">
                  <FaStar className="text-amber-200" /> Featured
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0 shadow-md">
                    {getInitials(advisor.name || 'FA')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-xl font-bold text-gray-900">{advisor.name}</h4>
                    <p className="text-amber-700 font-medium text-sm">{getSpecialtyLabel(advisor.professional_type)}</p>
                    {advisor.company && (
                      <p className="text-gray-500 text-sm mt-0.5">{advisor.company}</p>
                    )}
                  </div>
                </div>
                {advisor.bio && (
                  <p className="text-gray-600 text-sm mt-4 leading-relaxed line-clamp-3">{advisor.bio}</p>
                )}
                <div className="flex items-center flex-wrap gap-3 mt-4">
                  {advisor.city && (
                    <span className="inline-flex items-center gap-1 text-gray-500 text-sm">
                      <FaMapMarkerAlt className="text-amber-500" />
                      {advisor.city}{advisor.state ? `, ${advisor.state}` : ''}
                    </span>
                  )}
                  {advisor.years_experience && (
                    <span className="text-gray-500 text-sm">
                      {advisor.years_experience}+ yrs experience
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <p className="text-gray-600 mb-4">
            Are you a professional who helps families thrive? Join during our launch for Founding Advisor status.
          </p>
          <button
            onClick={() => navigate('/pro')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-semibold shadow-md hover:shadow-lg transition transform hover:scale-105"
            data-testid="become-founding-advisor-btn"
          >
            <FaAward /> Become a Founding Advisor
          </button>
        </div>
      </div>
    </section>
  );
};

// Benefit card with icons stacked above the text; wider boxes for a warmer feel
const BenefitCard = ({ icons, bgGradient, blobColor, title, description, testid }) => (
  <div
    className={`relative overflow-hidden bg-gradient-to-br ${bgGradient} px-8 py-10 sm:px-12 sm:py-12 rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-white/70 hover:-translate-y-1`}
    data-testid={testid}
  >
    {/* Soft decorative blob for depth */}
    <div className={`absolute -top-12 -right-12 w-48 h-48 ${blobColor} rounded-full opacity-30 blur-3xl pointer-events-none`} />

    <div className="relative flex flex-col items-center text-center">
      {/* Icons — stacked on TOP of the writing */}
      <div className="flex items-end justify-center gap-6 sm:gap-8 mb-6">
        {icons.map((item, idx) => {
          const Icon = item.icon;
          return (
            <div key={idx} className="flex flex-col items-center">
              <div
                className={`${item.color} drop-shadow-md transition-transform duration-300 hover:scale-110`}
                style={{ fontSize: item.size }}
              >
                <Icon />
              </div>
              {item.label && (
                <span className="mt-2 text-xs sm:text-sm font-medium text-gray-600">{item.label}</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Text */}
      <h4 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{title}</h4>
      <p className="text-gray-700 leading-relaxed text-base sm:text-lg max-w-2xl">{description}</p>
    </div>
  </div>
);

// Simple pricing teaser card
const PricingTeaserCard = ({ tierLabel, price, tagline, color, highlight, testid }) => (
  <div
    className={`bg-white rounded-2xl p-6 shadow-md border-2 ${highlight ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-100'} hover:shadow-xl transition`}
    data-testid={testid}
  >
    {highlight && (
      <div className="inline-block bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
        MOST POPULAR
      </div>
    )}
    <div className={`text-sm font-bold mb-2 ${color}`}>{tierLabel}</div>
    <div className="text-3xl font-semibold text-slate-600 mb-1">{price}</div>
    <p className="text-gray-600 text-sm">{tagline}</p>
  </div>
);

const IMAGES = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/14e2c146-1a48-4541-af20-3088c1dec714/images/392dfbf5e497e9987d10ee5ee197d8401517e16422f358d12fe44d1bda84523e.jpeg',
  together: 'https://static.prod-images.emergentagent.com/jobs/14e2c146-1a48-4541-af20-3088c1dec714/images/1a077bdd4832ac1ae918e735acb3c51a4f8ea57673fcc4d211ea2fb57fe7366c.jpeg',
  preserve: 'https://static.prod-images.emergentagent.com/jobs/14e2c146-1a48-4541-af20-3088c1dec714/images/a65c77fdb9ebe143ae991c1d30a6649b2f00730c610b1172b24fb69094a5fcd7.jpeg',
  connect: 'https://static.prod-images.emergentagent.com/jobs/14e2c146-1a48-4541-af20-3088c1dec714/images/054631011661bbff7046bf597ad92c0a70c5da98e4b073afb434178446a76c77.jpeg',
  plan: 'https://static.prod-images.emergentagent.com/jobs/14e2c146-1a48-4541-af20-3088c1dec714/images/ffcf8187441f2b729457f4ecb51bab00741562f8d53d136a03b50982ea6d6e92.jpeg',
  cta: 'https://static.prod-images.emergentagent.com/jobs/14e2c146-1a48-4541-af20-3088c1dec714/images/7e3d14fbb420f50ec10af5bf04b79b8146b0d0ed7040920352b674ae89bdbe7a.jpeg',
};

function Landing() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [familyCount, setFamilyCount] = useState(null);
  const [displayCount, setDisplayCount] = useState(0);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats`)
      .then((r) => r.json())
      .then((d) => { if (typeof d?.families === 'number') setFamilyCount(d.families); })
      .catch(() => {});
  }, []);

  // Social proof baseline: keep the counter hidden until this many families join,
  // then display a rounded "400+" style figure.
  const COUNT_BASELINE = 400;
  const showFamilyCount = familyCount != null && familyCount >= COUNT_BASELINE;
  const displayTarget = showFamilyCount ? Math.floor(familyCount / 100) * 100 : 0;

  useEffect(() => {
    if (!showFamilyCount) return;
    const duration = 1200;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayCount(Math.round(eased * displayTarget));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [showFamilyCount, displayTarget]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <BrandLogo className="w-9 h-9 sm:w-11 sm:h-11" testId="landing-header-logo" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Legacy Forge Family</h1>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={() => navigate('/features')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
              data-testid="header-features-btn"
            >
              Features
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
              data-testid="header-pricing-btn"
            >
              Pricing
            </button>
            <button
              onClick={() => navigate('/professionals/directory')}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium"
              data-testid="header-directory-btn"
            >
              Find Advisors
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 text-blue-600 hover:text-blue-700 font-medium"
              data-testid="header-login-btn"
            >
              Log In
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              data-testid="header-signup-btn"
            >
              Sign Up
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-gray-900"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg border-t z-50">
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-3">
              <button
                onClick={() => { navigate('/features'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg font-medium"
              >
                Features
              </button>
              <button
                onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg font-medium"
              >
                Pricing
              </button>
              <button
                onClick={() => { navigate('/professionals/directory'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-gray-600 hover:bg-gray-50 rounded-lg font-medium"
              >
                Find Advisors
              </button>
              <button
                onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-blue-600 hover:bg-blue-50 rounded-lg font-medium"
              >
                Log In
              </button>
              <button
                onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-center"
              >
                Sign Up
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero — phones: headline on white above photo; tablet/desktop: overlaid on the open sky */}
      <section className="relative bg-white" data-testid="hero-section">
        <div className="flex flex-col items-center text-center px-4 py-8 lg:absolute lg:inset-x-0 lg:top-0 lg:z-10 lg:py-0 lg:pt-16 lg:pl-[40%] lg:pr-[5%]">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 max-w-4xl mx-auto leading-tight"
            style={{ textShadow: '0 1px 16px rgba(255,255,255,0.6), 0 0 3px rgba(255,255,255,0.5)' }}
          >
            Build Your Family Legacy Together
          </h2>
          <p
            className="mt-3 lg:mt-4 text-base sm:text-lg lg:text-xl text-gray-800 max-w-xl lg:max-w-md mx-auto"
            style={{ textShadow: '0 1px 12px rgba(255,255,255,0.92)' }}
          >
            One simple place to plan reunions, strengthen bonds, and pass down what matters — across every generation.
          </p>
          <p
            className="mt-1 lg:mt-2 text-sm sm:text-base text-gray-700"
            style={{ textShadow: '0 1px 12px rgba(255,255,255,0.92)' }}
          >
            Always free to start. Upgrade only when you're ready.
          </p>
          <button
            onClick={() => navigate('/register')}
            className="mt-4 lg:mt-5 px-8 sm:px-10 py-3 sm:py-4 bg-blue-600 text-white text-base sm:text-lg rounded-lg hover:bg-blue-700 font-semibold shadow-lg transform hover:scale-105 transition"
            data-testid="hero-get-started-btn"
          >
            Get Started Free
          </button>
        </div>
        <img
          src={IMAGES.hero}
          alt="A large multicultural family of all generations gathered together in a sunlit wildflower meadow, smiling toward the viewer"
          className="block w-full h-auto object-cover"
          data-testid="hero-image"
        />
      </section>

      {/* Families joined counter */}
      {showFamilyCount && (
        <section className="bg-gradient-to-b from-white to-blue-50/50 border-b border-gray-100" data-testid="family-count-section">
          <div className="container mx-auto px-4 py-6 sm:py-8 flex flex-col items-center text-center">
            <div className="flex items-center justify-center gap-2">
              <FaUsers className="text-blue-600 text-xl sm:text-2xl" />
              <span className="text-3xl sm:text-4xl font-extrabold text-blue-600 tabular-nums" data-testid="family-count-number">
                {displayCount.toLocaleString()}+
              </span>
              <span className="text-lg sm:text-xl font-semibold text-gray-900">
                families
              </span>
            </div>
            <p className="mt-1 text-sm sm:text-base text-gray-600" data-testid="family-count-caption">
              building their legacy on Legacy Forge — and growing every day
            </p>
          </div>
        </section>
      )}

      {/* Three benefits - simplified */}
      <section className="container mx-auto px-4 py-12 sm:py-16" data-testid="benefits-section">
        <div className="text-center mb-10 sm:mb-14">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Three ways Legacy Forge helps your family
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Simple, focused tools that grow with you. Start free, upgrade when you're ready.
          </p>
        </div>

        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
          <BenefitCard
            icons={[
              { icon: CalendarDaysIcon, color: 'text-blue-500', size: '4rem', label: 'Calendar' },
              { icon: FaUsers, color: 'text-indigo-600', size: '4rem', label: 'Family' },
              { icon: CruiseShipIcon, color: 'text-sky-500', size: '4rem', label: 'Travel' },
            ]}
            bgGradient="from-blue-50 to-indigo-100"
            blobColor="bg-blue-400"
            title="Plan &amp; Connect"
            description="Coordinate reunions, weddings, anniversaries, and family trips — share a calendar, message each other, and keep everyone in the loop in one place."
            testid="benefit-plan"
          />
          <BenefitCard
            icons={[
              { icon: FaBook, color: 'text-amber-600', size: '4rem', label: 'Stories' },
              { icon: FaCamera, color: 'text-rose-500', size: '4rem', label: 'Photos' },
              { icon: FaHeart, color: 'text-rose-400', size: '3rem', label: 'Spiritual' },
            ]}
            bgGradient="from-amber-50 to-rose-100"
            blobColor="bg-amber-400"
            title="Preserve Your Story"
            description="Photos, videos, timeline, prayers, and legacy stories — save what matters and pass it on to future generations."
            testid="benefit-preserve"
          />
          <BenefitCard
            icons={[
              { icon: FaMoneyBillWave, color: 'text-emerald-600', size: '4rem', label: 'Wealth' },
              { icon: FaSeedling, color: 'text-green-600', size: '3.7rem', label: 'Growth' },
            ]}
            bgGradient="from-emerald-50 to-green-100"
            blobColor="bg-emerald-400"
            title="Build Wealth Together"
            description="Financial education, family contributions, and access to trusted advisors so your family can grow generational wealth."
            testid="benefit-wealth"
          />
        </div>

        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/features')}
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-blue-700 border-2 border-blue-600 rounded-full hover:bg-blue-600 hover:text-white font-semibold text-base sm:text-lg shadow-md hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            data-testid="see-all-features-btn"
          >
            See All Features <FaArrowRight />
          </button>
        </div>
      </section>

      {/* Photo feature band — life on Legacy Forge */}
      <section className="container mx-auto px-4 py-8 sm:py-12" data-testid="story-band-section">
        <div className="space-y-14 sm:space-y-20 max-w-6xl mx-auto">
          {/* Share together */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="relative">
              <img src={IMAGES.together} alt="A multi-generational family sharing photos together on a tablet at home" loading="lazy" className="w-full h-auto rounded-3xl shadow-xl object-cover ring-1 ring-black/5" data-testid="story-image-together" />
            </div>
            <div>
              <span className="inline-block text-xs font-semibold tracking-wide uppercase text-indigo-700 bg-indigo-100 px-3 py-1 rounded-full mb-3">Share together</span>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Enjoy your memories on any device</h3>
              <p className="text-gray-600 text-base sm:text-lg">From the living room to across the country, everyone can view and add to your family's photos, videos, and stories — together, on the phone, tablet, or laptop.</p>
            </div>
          </div>
          {/* Preserve stories */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <span className="inline-block text-xs font-semibold tracking-wide uppercase text-amber-700 bg-amber-100 px-3 py-1 rounded-full mb-3">Preserve your story</span>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Keep every memory in one place</h3>
              <p className="text-gray-600 text-base sm:text-lg">Photos, videos, recipes, and the stories behind them — gathered where every generation can find them, from grandparents to grandkids.</p>
            </div>
            <div className="order-1 md:order-2 relative">
              <img src={IMAGES.preserve} alt="A grandfather and grandchild looking through a printed family photo album with a laptop nearby" loading="lazy" className="w-full h-auto rounded-3xl shadow-xl object-cover ring-1 ring-black/5" data-testid="story-image-preserve" />
            </div>
          </div>
          {/* Stay connected */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="relative">
              <img src={IMAGES.connect} alt="A multi-generational family gathering and laughing together outdoors" loading="lazy" className="w-full h-auto rounded-3xl shadow-xl object-cover ring-1 ring-black/5" data-testid="story-image-connect" />
            </div>
            <div>
              <span className="inline-block text-xs font-semibold tracking-wide uppercase text-blue-700 bg-blue-100 px-3 py-1 rounded-full mb-3">Stay connected</span>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Bring the whole family together</h3>
              <p className="text-gray-600 text-base sm:text-lg">Plan reunions and celebrations, share a family calendar, and keep everyone in the loop — no matter how far apart you live.</p>
            </div>
          </div>
          {/* Plan the future */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <span className="inline-block text-xs font-semibold tracking-wide uppercase text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full mb-3">Plan the future</span>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Build and protect what you pass down</h3>
              <p className="text-gray-600 text-base sm:text-lg">Organize important documents, coordinate family finances, and tap trusted advisors — so your family grows stronger with every generation.</p>
            </div>
            <div className="order-1 md:order-2 relative">
              <img src={IMAGES.plan} alt="Family members planning together at a table with a laptop and organized documents" loading="lazy" className="w-full h-auto rounded-3xl shadow-xl object-cover ring-1 ring-black/5" data-testid="story-image-plan" />
            </div>
          </div>
        </div>
      </section>

      {/* Simple pricing teaser */}
      <section className="bg-white py-14 sm:py-16" data-testid="pricing-teaser-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Plans that grow with your family
            </h3>
            <p className="text-gray-600">
              Start free. Upgrade anytime.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto">
            <PricingTeaserCard
              tierLabel="⭐ FREE"
              price="$0"
              tagline="Core family tools to get connected."
              color="text-gray-700"
              testid="tier-teaser-free"
            />
            <PricingTeaserCard
              tierLabel="🌱 ROOTED"
              price="$4.99/mo"
              tagline="Build structure: dues, governance, prayer."
              color="text-emerald-700"
              testid="tier-teaser-rooted"
            />
            <PricingTeaserCard
              tierLabel="🌿 GROWING"
              price="$9.99/mo"
              tagline="Mentorship, values, deeper connection."
              color="text-blue-700"
              highlight
              testid="tier-teaser-growing"
            />
            <PricingTeaserCard
              tierLabel="🌳 FLOURISHING"
              price="$14.99/mo"
              tagline="Professionals, consultations, storytelling."
              color="text-amber-700"
              testid="tier-teaser-flourishing"
            />
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow-md"
              data-testid="see-full-pricing-btn"
            >
              See Full Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Founding Advisor Spotlight */}
      <FoundingAdvisorSpotlight navigate={navigate} />

      {/* Final CTA */}
      <section className="bg-blue-600 py-16" data-testid="final-cta-section">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10 items-center max-w-6xl mx-auto">
            <div className="relative order-2 lg:order-1">
              <img
                src={IMAGES.cta}
                alt="A large multi-generational family portrait together at home in warm light"
                loading="lazy"
                className="w-full h-auto rounded-3xl shadow-2xl object-cover ring-1 ring-white/10"
                data-testid="cta-image"
              />
            </div>
            <div className="text-center lg:text-left order-1 lg:order-2">
              <h3 className="text-3xl font-bold text-white mb-4">
                Start Building Your Family's Future Today
              </h3>
              <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto lg:mx-0">
                Join families preserving their legacy, building wealth, and strengthening bonds across generations.
              </p>
              <button
                onClick={() => navigate('/register')}
                className="px-8 py-4 bg-white text-blue-600 text-lg rounded-lg hover:bg-gray-100 font-semibold shadow-lg transform hover:scale-105 transition"
                data-testid="cta-sign-up-btn"
              >
                Sign Up Now — It's Free
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
                <BrandLogo className="w-9 h-9" testId="landing-footer-logo" />
                <h3 className="text-xl font-bold">Legacy Forge Family</h3>
              </div>
              <p className="text-gray-300 text-sm">Building families. Preserving legacies. Empowering generations.</p>
            </div>

            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm">
              <button
                onClick={() => navigate('/features')}
                className="text-gray-300 hover:text-white transition"
              >
                Features
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="text-gray-300 hover:text-white transition"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/professionals/directory')}
                className="text-gray-300 hover:text-white transition"
              >
                Find Advisors
              </button>
              <button
                onClick={() => navigate('/terms')}
                className="text-gray-300 hover:text-white transition"
              >
                Terms of Service
              </button>
              <button
                onClick={() => navigate('/privacy')}
                className="text-gray-300 hover:text-white transition"
              >
                Privacy Policy
              </button>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-700 text-center">
            <p className="text-gray-400 text-xs sm:text-sm">
              Reunion Planning &middot; Financial Empowerment &middot; Mentorship &middot; Spiritual Heritage
            </p>
            <p className="text-gray-500 text-xs mt-2">&copy; 2025 Legacy Forge Family. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
